import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  Wallet, 
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Lightbulb,
  AlertTriangle,
  PiggyBank,
  X,
  Gift,
  Copy,
  Check
} from 'lucide-react';
import { dashboardAPI, accountsAPI } from '../lib/api';
import api from '../lib/api';
import { formatCurrency, formatDate, getAmountClass, getStatusBadgeClass } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useOrg } from '../contexts/OrgContext';
import { useAuth } from '../contexts/AuthContext';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import WelcomeWizard from '../components/WelcomeWizard';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const COLORS = ['#D94E18', '#18181B', '#71717A', '#A1A1AA', '#D4D4D8'];

export default function DashboardPage() {
  const navigate = useNavigate();
  const { org } = useOrg();
  const { user } = useAuth();
  const entityName = org?.company_name || user?.name || 'Dashboard';
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [hasAccounts, setHasAccounts] = useState(false);
  const [insights, setInsights] = useState([]);
  const [insightsDismissed, setInsightsDismissed] = useState(false);
  const [referralCode, setReferralCode] = useState(null);
  const [codeCopied, setCodeCopied] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      // Check if accounts exist
      const accountsRes = await accountsAPI.getAll();
      setHasAccounts(accountsRes.data.length > 0);
      
      if (accountsRes.data.length > 0) {
        const res = await dashboardAPI.get();
        setData(res.data);
        // Fetch AI insights if enabled
        try {
          const insightsRes = await api.get('/dashboard/insights');
          if (insightsRes.data.insights?.length) setInsights(insightsRes.data.insights);
        } catch {}
        // Load referral code
        try {
          const refRes = await api.get('/gtm/referrals/my-code');
          if (refRes.data?.code) setReferralCode(refRes.data);
        } catch {}
      }
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const { containerRef: pullRef, pullIndicator } = usePullToRefresh(loadDashboard);

  const handleSeedAccounts = async () => {
    try {
      await accountsAPI.seedDefaults();
      await loadDashboard();
    } catch (error) {
      console.error('Failed to seed accounts:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#D94E18] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Show setup prompt if no accounts
  if (!hasAccounts) {
    return (
      <div className="max-w-2xl mx-auto mt-12 text-center">
        <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Wallet className="w-8 h-8 text-[#D94E18]" />
        </div>
        <h2 className="text-2xl font-bold text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Welcome to kaya.ai
        </h2>
        <p className="text-zinc-500 mb-8">
          finance made easy with ai. Let's get started by setting up your chart of accounts.
        </p>
        <Button
          onClick={handleSeedAccounts}
          data-testid="seed-accounts-btn"
          className="bg-[#D94E18] hover:bg-[#C2410C] text-white px-8"
        >
          Set Up Default Accounts
        </Button>
      </div>
    );
  }

  return (
    <div ref={pullRef} className="space-y-6 animate-fade-in">
      {pullIndicator}
      <WelcomeWizard />
      {/* KPI Cards */}
      <div className="dashboard-grid">
        <Card className="kpi-card" data-testid="kpi-cash-balance">
          <div className="flex items-center gap-2 mb-1">
            <Wallet className="w-4 h-4 text-emerald-600" />
            <p className="text-xs text-zinc-500">Cash Balance</p>
          </div>
          <p className="kpi-value">{formatCurrency(data?.cash_balance || 0)}</p>
        </Card>

        <Card className="kpi-card" data-testid="kpi-income">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-emerald-600" />
            <p className="text-xs text-zinc-500">Income This Month</p>
          </div>
          <p className="kpi-value text-emerald-600">{formatCurrency(data?.income_this_month || 0)}</p>
        </Card>

        <Card className="kpi-card" data-testid="kpi-expenses">
          <div className="flex items-center gap-2 mb-1">
            <TrendingDown className="w-4 h-4 text-red-500" />
            <p className="text-xs text-zinc-500">Expenses This Month</p>
          </div>
          <p className="kpi-value text-red-600">{formatCurrency(data?.expenses_this_month || 0)}</p>
        </Card>

        <Card className="kpi-card" data-testid="kpi-pending">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-amber-500" />
            <p className="text-xs text-zinc-500">Pending</p>
          </div>
          <p className="kpi-value">{data?.pending_transactions || 0}</p>
          {data?.pending_transactions > 0 && (
            <Button
              variant="link"
              onClick={() => navigate('/transactions')}
              className="mt-1 p-0 h-auto text-xs text-[#D94E18]"
              data-testid="review-pending-btn"
            >
              Review now
            </Button>
          )}
        </Card>
      </div>

      {/* AI Proactive Insights */}
      {insights.length > 0 && !insightsDismissed && (
        <Card className="bg-amber-50/50 border-amber-200" data-testid="ai-insights-card">
          <div className="p-3 md:p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-semibold text-zinc-800">Kaya Insights</span>
              </div>
              <button onClick={() => setInsightsDismissed(true)} className="text-zinc-400 hover:text-zinc-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-2">
              {insights.map((insight) => {
                const Icon = insight.icon === 'savings' ? PiggyBank : insight.icon === 'alert' ? AlertTriangle : insight.icon === 'trending_down' ? TrendingDown : TrendingUp;
                return (
                  <div key={insight.title} className="flex items-start gap-2 text-sm text-zinc-700">
                    <Icon className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <span><strong>{insight.title}:</strong> {insight.text}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Trends */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-bold">Monthly Trends</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data?.monthly_trends || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#71717A" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#71717A" width={50} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #E4E4E7',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                    formatter={(value) => formatCurrency(value)}
                  />
                  <Bar dataKey="income" fill="#10B981" name="Income" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="expenses" fill="#D94E18" name="Expenses" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Expenses by Category */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-bold">Expenses by Category</CardTitle>
          </CardHeader>
          <CardContent>
            {data?.expenses_by_category?.length > 0 ? (
              <div className="h-64">
                <div className="flex flex-col sm:flex-row items-center h-full">
                  <ResponsiveContainer width="100%" height={160} className="sm:hidden">
                    <PieChart>
                      <Pie
                        data={data.expenses_by_category}
                        dataKey="amount"
                        nameKey="category"
                        cx="50%"
                        cy="50%"
                        outerRadius={60}
                        innerRadius={30}
                      >
                        {data.expenses_by_category.map((entry, index) => (
                          <Cell key={entry.category} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="hidden sm:block w-1/2 h-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={data.expenses_by_category}
                          dataKey="amount"
                          nameKey="category"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          innerRadius={40}
                        >
                          {data.expenses_by_category.map((entry, index) => (
                            <Cell key={entry.category} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="w-full sm:flex-1 space-y-2 mt-2 sm:mt-0">
                    {data.expenses_by_category.slice(0, 5).map((item, index) => (
                      <div key={item.category} className="flex items-center gap-2 text-sm">
                        <div 
                          className="w-3 h-3 rounded-full flex-shrink-0" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="text-zinc-600 flex-1 truncate text-xs sm:text-sm">{item.category}</span>
                        <span className="font-mono text-zinc-900 text-xs sm:text-sm">{formatCurrency(item.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-zinc-400">
                No expense data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Transactions */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base font-bold">Recent Transactions</CardTitle>
          <Button 
            variant="ghost" 
            onClick={() => navigate('/transactions')}
            className="text-[#D94E18]"
            data-testid="view-all-transactions-btn"
          >
            View all
          </Button>
        </CardHeader>
        <CardContent>
          {data?.recent_transactions?.length > 0 ? (
            <div className="space-y-1">
              {data.recent_transactions.slice(0, 5).map((txn) => (
                <div 
                  key={txn.id} 
                  className="flex items-center justify-between py-3 px-3 sm:px-4 border-b border-zinc-100 hover:bg-zinc-50/50 transition-colors rounded-md"
                  data-testid={`recent-txn-${txn.id}`}
                >
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      txn.amount >= 0 ? 'bg-emerald-100' : 'bg-red-100'
                    }`}>
                      {txn.amount >= 0 ? (
                        <ArrowUpRight className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <ArrowDownRight className="w-4 h-4 text-red-600" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-zinc-900 truncate">
                        {txn.normalized_merchant || txn.description}
                      </p>
                      <p className="text-xs text-zinc-400">{formatDate(txn.date)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 ml-2">
                    <Badge variant="outline" className={`${getStatusBadgeClass(txn.status)} hidden sm:inline-flex`}>
                      {txn.status}
                    </Badge>
                    <span className={`font-mono text-sm ${getAmountClass(txn.amount)}`}>
                      {formatCurrency(txn.amount, txn.currency)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-12 text-center text-zinc-400">
              <p>No transactions yet</p>
              <Button 
                variant="link" 
                onClick={() => navigate('/upload')}
                className="text-[#D94E18] mt-2"
              >
                Upload your first statement
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Referral Card */}
      {referralCode && (
        <Card className="border border-[#D94E18]/20 bg-gradient-to-r from-[#D94E18]/5 to-transparent" data-testid="referral-card">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3 mb-2">
              <Gift className="w-5 h-5 text-[#D94E18]" />
              <span className="text-sm font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Refer a friend, earn a free month</span>
            </div>
            <p className="text-xs text-zinc-500 mb-3">Share your code and when they subscribe, you both win.</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-zinc-100 text-[#D94E18] font-mono text-sm px-3 py-1.5 rounded-lg border border-zinc-200">
                {referralCode.code}
              </code>
              <Button
                size="sm"
                variant="outline"
                className="border-zinc-200 text-zinc-600"
                data-testid="copy-referral-code"
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/login?ref=${referralCode.code}`);
                  setCodeCopied(true);
                  setTimeout(() => setCodeCopied(false), 2000);
                }}
              >
                {codeCopied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
